import is from '@sindresorhus/is';
import type { ReactElement } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import type { JobProfileViewId } from '../../company-settings/constants';
import { jobProfileView, sidebar } from '../../company-settings/constants';
import { useActivateManagedTemplate } from '../../company-settings/tabs/job-architecture-tab/job-profile/api/use-activate-managed-template';
import { useCustomTemplate } from '../../company-settings/tabs/job-architecture-tab/job-profile/api/use-custom-template';
import {
    resolveManagedTemplateToActivate,
    useManagedTemplates,
} from '../../company-settings/tabs/job-architecture-tab/job-profile/api/use-managed-template';
import { useJobProfileTemplateErrorSnackbar } from '../../company-settings/tabs/job-architecture-tab/job-profile/utils/use-job-profile-template-error-snackbar';
import type { DefineTemplateOption } from '../components/DefineJobProfileTemplateDialog';
import { DefineJobProfileTemplateDialog } from '../components/DefineJobProfileTemplateDialog';
import { useNavigateToSettings } from './use-navigate-to-settings';

type RequireJobProfileTemplate = {
    readonly dialog: ReactElement;
    readonly requireJobProfileTemplate: (run: () => void | Promise<void>) => void;
};

export const useRequireJobProfileTemplate = (
    jobArchitectureId: string,
    enabled: boolean = true,
): RequireJobProfileTemplate => {
    const customTemplateQuery = useCustomTemplate(jobArchitectureId, enabled);
    const managedTemplatesQuery = useManagedTemplates(enabled);
    const managedTemplates = managedTemplatesQuery.data;
    const isLoadingTemplates = enabled && (managedTemplatesQuery.isPending || customTemplateQuery.isPending);
    const { activate, isPending } = useActivateManagedTemplate(jobArchitectureId, enabled);
    const { navigateToSettings } = useNavigateToSettings(jobArchitectureId);
    const showJobProfileTemplateError = useJobProfileTemplateErrorSnackbar();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const pendingRunRef = useRef<(() => void | Promise<void>) | null>(null);

    const close = useCallback((): void => {
        setIsDialogOpen(false);
        pendingRunRef.current = null;
    }, []);

    const runPending = useCallback(async (): Promise<void> => {
        const pendingRun = pendingRunRef.current;
        pendingRunRef.current = null;
        setIsDialogOpen(false);

        if (pendingRun !== null) {
            await pendingRun();
        }
    }, []);

    const requireJobProfileTemplate = useCallback(
        (action: () => void | Promise<void>): void => {
            if (!is.nullOrUndefined(customTemplateQuery.data)) {
                action();
                return;
            }

            pendingRunRef.current = action;
            setIsDialogOpen(true);
        },
        [customTemplateQuery.data],
    );

    const leaveForSettingsView = useCallback(
        (view: JobProfileViewId): void => {
            navigateToSettings({ jobProfileView: view, sidebar: sidebar.jobArchitecture.jobProfile });
            close();
        },
        [close, navigateToSettings],
    );

    const activateOrReport = useCallback(
        async (managedTemplateId: string): Promise<boolean> => {
            try {
                if (await activate(managedTemplateId)) {
                    return true;
                }
                showJobProfileTemplateError(new Error('Failed to activate the job profile template'));
            } catch (error) {
                showJobProfileTemplateError(error);
            }

            return false;
        },
        [activate, showJobProfileTemplateError],
    );

    const handleContinue = useCallback(
        async (option: DefineTemplateOption): Promise<void> => {
            if (option === 'custom') {
                leaveForSettingsView(jobProfileView.createCustom);
                return;
            }

            if (isLoadingTemplates) {
                return;
            }

            if (customTemplateQuery.isError) {
                showJobProfileTemplateError(customTemplateQuery.error);
                return;
            }

            if (!is.nullOrUndefined(customTemplateQuery.data)) {
                await runPending();
                return;
            }

            const activeManagedTemplates = managedTemplates ?? [];
            if (is.emptyArray(activeManagedTemplates)) {
                showJobProfileTemplateError(new Error('No active Cobrainer job profile template'));
                return;
            }

            const templateToActivate = resolveManagedTemplateToActivate(activeManagedTemplates);
            if (is.null(templateToActivate)) {
                leaveForSettingsView(jobProfileView.configure);
                return;
            }

            if (await activateOrReport(templateToActivate.id)) {
                await runPending();
            }
        },
        [
            activateOrReport,
            customTemplateQuery.data,
            customTemplateQuery.error,
            customTemplateQuery.isError,
            isLoadingTemplates,
            leaveForSettingsView,
            managedTemplates,
            runPending,
            showJobProfileTemplateError,
        ],
    );

    const dialog = useMemo(
        () => (
            <DefineJobProfileTemplateDialog
                isActivating={isPending}
                isLoadingTemplates={isLoadingTemplates}
                open={isDialogOpen}
                onClose={close}
                onContinue={handleContinue}
                onNavigateToSettings={() => navigateToSettings({ sidebar: sidebar.jobArchitecture.jobProfile })}
            />
        ),
        [close, handleContinue, isDialogOpen, isLoadingTemplates, isPending, navigateToSettings],
    );

    return { dialog, requireJobProfileTemplate };
};
